import { LoadingService } from '@services/ui/loading.service';
import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpHeaders,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ToastrService } from '@services/user/toastr.service';
import { GenericResponse } from '@models/common/Response';
import { ResponseDto } from '@models/common/ResponseDto';
import { ResponseTypeEnum } from '@models/Enum/ResponseTypeEnum';
import { ResponseErrorDto } from '@models/common/ResponseErrorDto';
import { UserDataApi } from '@models/RutersApi';
import { ModalService } from '@services/ui/modal.service';

@Injectable({
  providedIn: 'root',
})
export class WebService {
  private static readonly TENANT_INVALID_USER_ERROR = 'exception.token.invalid.user.not.found.in.tenant';
  private static readonly PROCESS_TRANSFER_ERROR = 'exception.process.transfer';
  private tenantRecoveryInProgress = false;

  constructor(
    private http: HttpClient,
    private router: Router,
    private toastrService: ToastrService,
    private loadingService: LoadingService,
    private modalService: ModalService,
  ) {}

  uploadFile<T>(endpoint: string, file: File): Observable<number | T> {
    const formdata: FormData = new FormData();
    formdata.append('file', file);

    const req = new HttpRequest('POST', endpoint, formdata, {
      reportProgress: true,
      responseType: 'json',
    });

    return this.http.request<ResponseDto<T>>(req).pipe(
      map((event: HttpEvent<ResponseDto<T>>) => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          return Math.round((event.loaded / event.total) * 100);
        } else if (event instanceof HttpResponse) {
          if (this.isTenantInvalidUserError(event.body)) {
            throw new HttpErrorResponse({
              error: event.body,
              status: 400,
              statusText: WebService.TENANT_INVALID_USER_ERROR,
              url: endpoint,
            });
          }
          return event.body!.data! as T;
        }
        return 0;
      }),
      catchError((res) => this.handleHttpError(res, undefined, endpoint)),
    );
  }

  dowloadFile(endpoint: string, header: HttpHeaders | {}, filename: string, clientErrorCb?: any): Observable<any> {
    return of(
      this.http
        .get(endpoint, {
          headers: header,
          observe: 'response',
          responseType: 'blob',
        })
        .pipe(
          catchError((res) => {
            clientErrorCb && clientErrorCb(res);
            return throwError({
              error: res,
            });
          }),
        )
        .subscribe((responce) => {
          if (responce?.body?.type) {
            const blob = new Blob([responce.body], {
              type: responce.body.type.toString(),
            });
            const url = window.URL.createObjectURL(blob);
            const downloadLink = document.createElement('a');
            downloadLink.href = url;
            downloadLink.download = filename;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
          }
        }),
    );
  }

  httpPostWithHeaders<T>(endpoint: string, body: string, options: any): Observable<T> {
    return this.http.post<ResponseDto<T>>(endpoint, body, options).pipe(
      map((res) => this.handleResponse<T>(res, undefined, endpoint)),
      catchError((res) => this.handleHttpError(res, undefined, endpoint)),
    );
    /*.pipe(
      map((res : T) => {
        return res;
      }));*/
  }

  httpGetWithHeaders<T>(endpoint: string, headers: any): Observable<ResponseDto<T>> {
    return this.http.get<ResponseDto<T>>(endpoint, headers).pipe(
      map((res: any) => {
        if (this.isTenantInvalidUserError(res)) {
          throw new HttpErrorResponse({
            error: res,
            status: 400,
            statusText: WebService.TENANT_INVALID_USER_ERROR,
            url: endpoint,
          });
        }
        return res;
      }),
      catchError((res) => this.handleHttpError(res, undefined, endpoint)),
    );
  }

  httpGetWithHeaders2<T>(endpoint: string, headers: any): Observable<T> {
    return this.http.get<ResponseDto<T>>(endpoint, headers).pipe(
      map((res: any) => {
        if (this.isTenantInvalidUserError(res)) {
          throw new HttpErrorResponse({
            error: res,
            status: 400,
            statusText: WebService.TENANT_INVALID_USER_ERROR,
            url: endpoint,
          });
        }
        return res;
      }),
      catchError((res) => this.handleHttpError(res, undefined, endpoint)),
    );
  }

  httpGet<T>(endpoint: string, params?: any, cb?: any): Observable<T> {
    return this.http.get<ResponseDto<T>>(endpoint, params).pipe(
      map((res) => this.handleResponse<T>(res, cb, endpoint)),
      catchError((res) => this.handleHttpError(res, cb, endpoint)),
    );
  }

  httpDelete<T>(endpoint: string, data?: any): Observable<T> {
    let httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
      body: data ? JSON.stringify(data) : undefined,
    };
    return this.http.delete<ResponseDto<T>>(endpoint, httpOptions).pipe(
      map((res) => this.handleResponse<T>(res)),
      catchError((res) => this.handleHttpError(res)),
    );
  }

  httpPost<T>(endpoint: string, data: any, clientErrorCb?: any): Observable<T> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    };
    return this.http.post<T>(endpoint, JSON.stringify(data), httpOptions).pipe(
      map((res) => this.handleResponse<T>(res, clientErrorCb)),
      catchError((res) => {
        return this.handleHttpError(res, clientErrorCb);
      }),
    );
  }

  httpPostFormData<T>(endpoint: string, data: any, clientErrorCb?: any, options?: any): Observable<T> {
    return this.http.post<T>(endpoint, data, options).pipe(
      map((res) => this.handleResponse<T>(res, clientErrorCb)),
      catchError((res) => this.handleHttpError(res, clientErrorCb)),
    );
  }

  httpPut<T>(endpoint: string, data: any): Observable<T> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    };
    return this.http.put<T>(endpoint, JSON.stringify(data), httpOptions).pipe(
      map((res) => this.handleResponse<T>(res)),
      catchError((res) => this.handleHttpError(res)),
    );
  }

  httpPutFormData<T>(endpoint: string, data: any): Observable<T> {
    return this.http.put<T>(endpoint, data).pipe(
      map((res) => this.handleResponse<T>(res)),
      catchError((res) => this.handleHttpError(res)),
    );
  }

  httpPatch<T>(endpoint: string, data: any, clientErrorCb: any): Observable<T> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    };
    return this.http.patch<T>(endpoint, JSON.stringify(data), httpOptions).pipe(
      map((res) => this.handleResponse<T>(res)),
      catchError((res): Observable<any> => this.handleHttpError(res, clientErrorCb)),
    );
  }

  private handleResponse<T>(res: any, clientErrorCb?: any, requestUrl?: string) {
    if (res === true) {
      return res;
    }
    const response = res as GenericResponse<T>;
    if (response?.result == true) {
      return response.data;
    } else {
      this.loadingService.forceStopAll();
      if (this.isTenantInvalidUserError(response)) {
        throw new HttpErrorResponse({
          error: response,
          status: 400,
          statusText: WebService.TENANT_INVALID_USER_ERROR,
          url: requestUrl,
        });
      }

      if (this.isProcessTransferError(response)) {
        throw new HttpErrorResponse({
          error: response,
          status: 400,
          statusText: WebService.PROCESS_TRANSFER_ERROR,
          url: requestUrl,
        });
      }

      if (this.isUserdataGenericException(requestUrl, response?.error_messages)) {
        throw new HttpErrorResponse({
          error: response,
          status: 400,
          statusText: 'generic.exception',
          url: requestUrl,
        });
      }

      if (clientErrorCb) {
        clientErrorCb(response);
        return response.data;
      } else {
        this.toastrService.notice(response.error_messages, response.msg_params, undefined, 'warning');
        return response.data;
      }
    }
  }

  // @ts-ignore
  private handleHttpError(errorResponse: HttpErrorResponse, clientErrorCb?: any, requestUrl?: string): Observable<any> {
    this.loadingService.forceStopAll();
    console.log('handleHttpError', errorResponse.message);
    const targetUrl = errorResponse?.url ?? requestUrl;

    if (errorResponse.message.endsWith('login')) {
      this.router.navigate(['/']);
      return of();
    }
    if (this.isTenantInvalidUserError(errorResponse?.error)) {
      return this.refreshUserdataAndReload();
    }
    if (this.isProcessTransferError(errorResponse?.error) || errorResponse?.statusText === WebService.PROCESS_TRANSFER_ERROR) {
      this.handleProcessTransferError();
      return EMPTY;
    }
    if (this.isUserdataRequest(targetUrl)) {
      if (errorResponse.status === 403) {
        this.router.navigate(['/error'], { queryParams: { code: 403 } });
      } else if (errorResponse.status === 503) {
        this.router.navigate(['/courtesy']);
      } else {
        this.router.navigate(['/error']);
      }
      return of();
    }

    if (errorResponse.status >= 400 && errorResponse.status < 500) {
      // A client-side or network error occurred. Handle it accordingly.
      const clientError = errorResponse.error as ResponseErrorDto;
      if (clientErrorCb) {
        // if (clientError.type === ResponseTypeEnum.VALIDATION_ERROR) {
        //   return of();
        // } else {
        //   //this.toastService.showErrorToast( this.translocoService.translate("toast.unexpectedErrorBody"));
        // }
        clientErrorCb(clientError);
      }
      return throwError(() => {
        error: errorResponse;
      });
    }
  }

  private isUserdataGenericException(requestUrl?: string, errorMessage?: string): boolean {
    return this.isUserdataRequest(requestUrl) && errorMessage?.includes('generic.exception') === true;
  }

  private isUserdataRequest(requestUrl?: string | null): boolean {
    return requestUrl?.includes('/users/self/userdata') === true;
  }

  private refreshUserdataAndReload(): Observable<any> {
    if (this.tenantRecoveryInProgress) {
      return of();
    }

    this.tenantRecoveryInProgress = true;
    this.toastrService.notice('tenantChangedReloadRequired', undefined, 2500, 'warning');

    return this.http.get<ResponseDto<any>>(UserDataApi.RETRIEVE_USER_DATA).pipe(
      map(() => {
        setTimeout(() => window.location.reload(), 1500);
        return undefined;
      }),
      catchError(() => {
        setTimeout(() => window.location.reload(), 1500);
        return of();
      }),
    );
  }

  private isTenantInvalidUserError(error: any): boolean {
    return this.collectErrorMessages(error).some((message) =>
      message.includes(WebService.TENANT_INVALID_USER_ERROR)
    );
  }

  private isProcessTransferError(error: any): boolean {
    return this.collectErrorMessages(error).some((message) =>
      message.includes(WebService.PROCESS_TRANSFER_ERROR)
    );
  }

  private handleProcessTransferError(): void {
    this.modalService.closeModal();
    this.toastrService.notice(WebService.PROCESS_TRANSFER_ERROR, undefined, 5000, 'warning');
    this.router.navigate(['/pages/home']);
  }

  private collectErrorMessages(error: any): string[] {
    if (!error) {
      return [];
    }
    if (typeof error === 'string') {
      return [error];
    }
    if (Array.isArray(error)) {
      return error.flatMap((item) => this.collectErrorMessages(item));
    }

    return [
      ...this.collectErrorMessages(error.error_messages),
      ...this.collectErrorMessages(error.error_code),
      ...this.collectErrorMessages(error.code),
      ...this.collectErrorMessages(error.messages),
      ...this.collectErrorMessages(error.message),
      ...this.collectErrorMessages(error.error),
    ];
  }
}
